/**
 * PATH: packages/kanban-host/src/KanbanFullEmailReader.tsx
 * FILE_ID: KANBANHOST-FULL-EMAIL-READER-001
 * PURPOSE: The host-injected FULL email reader (reader convergence arc).
 *          Lazy-mounts the Email Hub's canonical reading surface
 *          (EmailPreviewPane: search-in-email ⌘F, tags, right-click
 *          context menu, smart actions, assign/schedule rail, calendar
 *          cards, inline-image lightbox/markup/recovery) inside the
 *          kanban card drawer + board email rail.
 * WHY:     Operator 2026-07-06: "the email for physio business here has
 *          no options, I can't search it but also the tags the right
 *          click the ability to do all of the things you can do in the
 *          email hub is not here … the entire point of having it so that
 *          you render from the same source is moat." The drawer's
 *          in-package <EmailThreadView> fork had 3 of the hub reader's
 *          18 capabilities. @quotech/ui cannot import email-hub (cycle),
 *          so the full reader is injected via
 *          `KanbanEmailAdapter.FullEmailReader` — the same seam as
 *          InlineComposer / TagChips / DocumentEditorOverlay.
 * FLOW:
 *   ┌─────────┐   ┌───────────────────────┐   ┌────────────────────────┐
 *   │ emailId │ → │ detail GET ?scope=all │ → │ EmailPreviewPane +     │
 *   │ (+card) │   │ (react-query)         │   │ ContextMenu + Markup   │
 *   └─────────┘   └───────────────────────┘   └────────────────────────┘
 * DEPENDENCIES:
 *   - ./KanbanFullEmailReaderInner: the email-hub-importing body —
 *     ALWAYS lazy so the kanban entry chunk stays lean (same pattern as
 *     KanbanDocumentOverlay's PdfEditor mount).
 * RELATED:
 *   - packages/ui/src/kanban/api/adapters-email-components.ts:
 *     KanbanFullEmailReaderProps contract.
 *   - packages/ui/src/kanban/components/card-detail/CardEmailTab.tsx +
 *     packages/ui/src/kanban/components/KanbanEmailAllocationRail.tsx:
 *     the consumers (prefer this, fall back to EmailThreadView).
 */

import { Suspense, lazy } from 'react'
import { Loader2 } from 'lucide-react'

import type { KanbanFullEmailReaderProps } from '@quotech/ui/kanban'

const Inner = lazy(() => import('./KanbanFullEmailReaderInner'))

export function KanbanFullEmailReader(props: KanbanFullEmailReaderProps) {
  return (
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center gap-2 text-sm text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading reader…
        </div>
      }
    >
      <Inner {...props} />
    </Suspense>
  )
}

export default KanbanFullEmailReader
